/**
 * useDialogFocus — the shared keyboard contract for the application's modal dialogs
 * (PROD-UX-076 / FULL-0162).
 *
 * Before this hook, the helpdesk bulk/merge/split dialogs declared `role="dialog"` and an accessible
 * title but never moved keyboard focus into the dialog, never kept Tab inside it, and never returned
 * focus to the control that opened it. A keyboard user could open a destructive confirmation, keep
 * tabbing into the page behind it, and lose their place when it closed.
 *
 * The hook owns exactly three behaviours:
 *   1. on open, focus moves to the dialog's first focusable control (or the dialog itself);
 *   2. Tab / Shift+Tab cycle inside the dialog and cannot reach the obscured page;
 *   3. on close, focus returns to the element that was focused before the dialog opened.
 *
 * It is presentation and navigation only. It does not open, submit, approve, dismiss or authorize
 * anything: Escape calls the caller's existing close handler, and every action inside the dialog
 * still goes through the same server-enforced permission and approval path as before.
 */
import { useEffect, useRef } from "react";

// Native focusable content, minus anything the browser or the app has disabled or hidden.
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "audio[controls]",
  "video[controls]",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function isVisible(element) {
  if (!element || typeof element.getAttribute !== "function") return false;
  if (element.hasAttribute("hidden")) return false;
  if (element.getAttribute("aria-hidden") === "true") return false;
  // jsdom has no layout; the attribute checks above are the parts this suite can observe.
  return true;
}

/** The dialog's tabbable controls, in DOM order. Exported so a test can pin the boundary set. */
export function getFocusableElements(container) {
  if (!container || typeof container.querySelectorAll !== "function") return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isVisible);
}

/**
 * @param {{ current: HTMLElement | null }} containerRef  the dialog element itself
 * @param {{ active?: boolean, onClose?: (() => void) | null }} options
 */
export default function useDialogFocus(containerRef, { active = true, onClose = null } = {}) {
  // The close handler is usually an inline arrow; keeping it in a ref stops a parent re-render from
  // re-running the effect, which would yank focus back to the first field the user just left.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!active) return undefined;

    const container = containerRef.current;
    if (!container || typeof document === "undefined") return undefined;

    const previouslyFocused =
      document.activeElement && document.activeElement !== document.body ? document.activeElement : null;

    const initial = getFocusableElements(container)[0];
    if (initial) {
      initial.focus();
    } else {
      // A dialog with no controls still has to receive focus so the reader announces it.
      container.setAttribute("tabindex", "-1");
      container.focus();
    }

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        if (typeof onCloseRef.current === "function") {
          event.preventDefault();
          onCloseRef.current();
        }
        return;
      }
      if (event.key !== "Tab") return;

      const items = getFocusableElements(container);
      if (items.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;

      // Focus escaped (e.g. the browser moved it out): bring it back to the dialog's edge.
      if (!container.contains(current)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey && (current === first || current === container)) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (previouslyFocused && typeof previouslyFocused.focus === "function" && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
    // `containerRef` is stable for the component's lifetime; `active` is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
